import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { ColumnsType } from "antd/es/table/interface";
import { useLocation, useNavigate } from "react-router-dom";
import { FaCopy, FaLink, FaSync, FaUser } from "react-icons/fa";
import InvoicesService, { IInvoiceWithFilters } from "../../../../../../services/http/services/InvoicesService";
import useAuth from "../../../../../../hooks/useAuth";
import { EmitSuccessAlert } from "../../../../../../utils/EmitSuccessAlert";
import { AxiosError } from "axios";
import { EmitError } from "../../../../../../utils/EmitError";
import { useGetColumnSearchPropsData } from "../../../../../../hooks/FilterTable/date";
import { convertDataBrParaUS, convertMoedaBRDoJSNovo, formatDateStringToDateAndTime, poeValorComPontosEmMil } from "../../../../../../utils/Conversores";
import { useGetColumnSearchProps } from "../../../../../../hooks/FilterTable/stringClearFiltros";
import { EInvoiceStatus } from "../../../../../../enums";
import StatusTag from "../../../../../CityInvoices/components/StatusTag";
import { getLinkInvoice, normalizeLink, verifyLinkIsValid } from "../../../../../../utils/Functions";
import { notification, Tag, Tooltip } from "antd";
import { useIdColumn } from "../../../../../../hooks/useIdColumn";
import { ThemeContext } from "../../../../../../contexts/ThemeContext";
import { IActiveFilterItem } from "../../../../../../components/ActiveFiltersBar";
import TableActionButton from "../../../../../../components/TableActionButton";
import { getNextSortOrder } from "../../../../../../hooks/NextSortOrder";
import { getSortOrder } from "../../../../../../hooks/SortOrder";

export interface ITableData extends IInvoiceWithFilters {
    rowKey: number | undefined;
    appUserName: string;
    appUserId: number;
    createdAt: Date;
    date_issue: Date;
}

export type TypeSortInvoice = 'date_issue' | 'createdAt' | 'type' | 'status' | 'value';

export interface ITabInvoiceFilters {
    company: string | null;
    appUserName: string | null;
    type: string | null;
    date_issue: string | null;
    createdAt: string | null;
    status: any;
}

interface IProps {
    startPeriod: string | undefined;
    endPeriod: string | undefined;
}

export default function useTabInvoices({ startPeriod, endPeriod }: IProps) {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [tableData, setTableData] = useState<Array<ITableData>>([]);
    const [totalInvoices, setTotalInvoices] = useState<number>(0);
    const refFuncaoLimparFiltrosString = useRef<any>(null);
    const lastSorterRef = useRef<{ field: string; order: string } | any>({ field: 'date_issue', order: 'desc' });

    const navigate = useNavigate();
    const location = useLocation();
    const { user, activeCity } = useAuth();
    const isAdmin = user?.role?.id === 1;
    const { company } = location.state || {};

    const [tableParams, setTableParams] = useState({
        pagination: {
            current: 1,
            pageSize: 50,
        },
        filters: {
            company: company || null,
            appUserName: null,
            type: null,
            date_issue: null,
            createdAt: null,
            status: null,
        } as ITabInvoiceFilters,
        sorter: {
            field: 'date_issue' as TypeSortInvoice,
            order: 'desc' as 'asc' | 'desc',
        },
    });

    const handleTableChange = useCallback((pagination: any, filters: any, sorter: any) => {
        const newPage = pagination?.current || 1;
        const newSize = pagination?.pageSize || tableParams.pagination.pageSize;
        const sorterObj = Array.isArray(sorter) ? sorter[0] : sorter;

        const { nextSort, nextOrder } = getNextSortOrder(
            sorterObj,
            tableParams,
            newPage,
            lastSorterRef
        );

        setTableParams((prev) => ({
            pagination: {
                current: newPage,
                pageSize: newSize,
            },
            filters: {
                ...prev.filters,
                type: filters?.type ? filters.type[0] : null,
                appUserName: filters?.appUserName ? filters.appUserName[0] : null,
                company: filters?.company ? filters.company[0] : null,
                status: filters?.status && filters.status.length > 0 ? filters.status : null,
                date_issue: filters?.date_issue ? filters.date_issue[0] : null,
                createdAt: filters?.createdAt ? filters.createdAt[0] : null,
            },
            sorter: {
                field: (nextSort || prev.sorter.field) as TypeSortInvoice,
                order: (nextOrder || prev.sorter.order) as 'asc' | 'desc',
            },
        }));
    }, [tableParams]);

    const handlePaginationChange = useCallback((page: number, pageSize: number) => {
        setTableParams((prev) => ({
            ...prev,
            pagination: {
                current: page,
                pageSize,
            },
        }));
    }, []);

    const handleFilterClear = useCallback((field: keyof ITabInvoiceFilters) => {
        setTableParams((prev) => ({
            ...prev,
            pagination: { ...prev.pagination, current: 1 },
            filters: {
                ...prev.filters,
                [field]: null,
            },
        }));
    }, []);

    const handleClearAllFilters = useCallback(() => {
        setTableParams((prev) => ({
            ...prev,
            pagination: { ...prev.pagination, current: 1 },
            filters: {
                company: null,
                appUserName: null,
                type: null,
                date_issue: null,
                createdAt: null,
                status: null,
            },
        }));
    }, []);

    const activeFilterItems: Array<IActiveFilterItem | null> = [
        tableParams.filters.date_issue
            ? { key: 'date_issue', label: 'Data de Emissão', value: tableParams.filters.date_issue, onClear: () => handleFilterClear('date_issue') }
            : null,
        tableParams.filters.createdAt
            ? { key: 'createdAt', label: 'Data de Importação', value: tableParams.filters.createdAt, onClear: () => handleFilterClear('createdAt') }
            : null,
        tableParams.filters.appUserName
            ? { key: 'appUserName', label: 'Usuário', value: tableParams.filters.appUserName, onClear: () => handleFilterClear('appUserName') }
            : null,
        tableParams.filters.company
            ? { key: 'company', label: 'Empresa', value: tableParams.filters.company, onClear: () => handleFilterClear('company') }
            : null,
        tableParams.filters.type
            ? { key: 'type', label: 'Tipo', value: tableParams.filters.type, onClear: () => handleFilterClear('type') }
            : null,
        tableParams.filters.status
            ? {
                key: 'status',
                label: 'Status',
                value: Array.isArray(tableParams.filters.status) ? tableParams.filters.status.join(', ') : tableParams.filters.status,
                onClear: () => handleFilterClear('status')
            }
            : null,
    ];

    const reprocessInvoice = useCallback((invoiceId: number, appUserId: number) => {
        async function sendInvoiceToReprocess() {
            try {
                await InvoicesService.reprocessInvoice({
                    invoiceId,
                    appUserId: Number(appUserId),
                    cityId: activeCity?.id!
                });
                EmitSuccessAlert('Nota enviada para reprocessamento!');
            } catch (err) {
                if (err instanceof AxiosError && err.name === 'CanceledError') {
                    return;
                }
                EmitError(err, true, 'viewAppUserInvoices_asodkaskldasd');
            }
        }
        sendInvoiceToReprocess();
    }, [activeCity]);

    const idColumn = useIdColumn({
        show: isAdmin,
        ref: refFuncaoLimparFiltrosString,
        onClick: (id) => navigate(`/nota/detalhes/${id}`),
    });

    const columns: ColumnsType<ITableData> = [
        idColumn ? { ...idColumn, width: 105 } : null,
        {
            title: 'Data de Emissão',
            dataIndex: 'date_issue',
            key: 'date_issue',
            sorter: true,
            sortOrder: getSortOrder('date_issue', tableParams.sorter),
            showSorterTooltip: false,
            ...useGetColumnSearchPropsData({
                dataIndex: 'date_issue',
                ref: refFuncaoLimparFiltrosString,
                onFilterClear: () => handleFilterClear('date_issue'),
                initialValue: tableParams.filters.date_issue,
            }),
            render: (data: any) => (
                <span style={{ color: isDark ? '#8c8c8c' : '#64748b', fontSize: 13, whiteSpace: 'nowrap' }}>
                    {data ? formatDateStringToDateAndTime(data) : '--'}
                </span>
            ),
            width: 190,
        },
        {
            title: 'Data de Importação',
            dataIndex: 'createdAt',
            key: 'createdAt',
            sorter: true,
            sortOrder: getSortOrder('createdAt', tableParams.sorter),
            showSorterTooltip: false,
            ...useGetColumnSearchPropsData({
                dataIndex: 'createdAt',
                ref: refFuncaoLimparFiltrosString,
                onFilterClear: () => handleFilterClear('createdAt'),
                initialValue: tableParams.filters.createdAt,
            }),
            render: (data: any) => (
                <span style={{ color: isDark ? '#8c8c8c' : '#64748b', fontSize: 13, whiteSpace: 'nowrap' }}>
                    {data ? formatDateStringToDateAndTime(data) : '--'}
                </span>
            ),
            width: 190,
        },
        {
            title: 'Tipo',
            dataIndex: 'type',
            key: 'type',
            align: 'center' as const,
            sorter: true,
            sortOrder: getSortOrder('type', tableParams.sorter),
            showSorterTooltip: false,
            filters: [
                { text: 'NFS', value: 'NFS' },
                { text: 'NFC', value: 'NFC' },
                { text: 'NFE', value: 'NFE' },
            ],
            filteredValue: tableParams.filters.type ? [tableParams.filters.type] : null,
            filterMultiple: false,
            render: (_: any, { type }: ITableData) => (
                <Tag color="cyan" style={{ fontWeight: 600 }}>{type ?? '--'}</Tag>
            ),
            width: 100,
        },
        {
            title: 'Usuário',
            dataIndex: 'appUserName',
            key: 'appUserName',
            ...useGetColumnSearchProps({ dataIndex: 'appUserName', ref: refFuncaoLimparFiltrosString }),
            render: (name: string) => (
                <Tooltip title={name} placement="topLeft">
                    <span
                        style={{
                            fontWeight: 600,
                            color: isDark ? '#f0f0f0' : '#1e293b',
                            fontSize: 13,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            wordBreak: 'break-word',
                            lineHeight: 1.3,
                        }}
                    >
                        {name || '--'}
                    </span>
                </Tooltip>
            ),
            width: 140,
        },
        {
            title: 'Valor',
            dataIndex: 'value',
            key: 'value',
            align: 'right' as const,
            sorter: true,
            sortOrder: getSortOrder('value', tableParams.sorter),
            showSorterTooltip: false,
            render: (_: any, { value }: ITableData) => (
                <Tooltip color="#389e0d" title="Valor total da nota">
                    <span style={{ fontWeight: 600, color: isDark ? '#52c41a' : '#237804', fontSize: 13, whiteSpace: 'nowrap' }}>
                        R$ {convertMoedaBRDoJSNovo(value)}
                    </span>
                </Tooltip>
            ),
            width: 130,
        },
        {
            title: 'Bilhetes',
            dataIndex: 'tickets',
            key: 'tickets',
            align: 'center' as const,
            render: (_: any, { tickets }: ITableData) => {
                const count = tickets?.length || 0;
                return (
                    <Tooltip color="#1677ff" title={`${count} bilhete(s) gerado(s)`}>
                        <span style={{ fontWeight: 700, color: count > 0 ? '#1677ff' : (isDark ? '#595959' : '#8c8c8c'), fontSize: 14 }}>
                            {poeValorComPontosEmMil(count)}
                        </span>
                    </Tooltip>
                );
            },
            width: 110,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            align: 'center' as const,
            sorter: true,
            sortOrder: getSortOrder('status', tableParams.sorter),
            showSorterTooltip: false,
            filters: [
                { text: 'Concluído', value: 'Concluído' },
                { text: 'Com erro', value: EInvoiceStatus.error },
                { text: 'Alerta', value: EInvoiceStatus.warning },
                { text: 'Em processamento', value: EInvoiceStatus.processing },
                { text: 'Reprocessando', value: EInvoiceStatus.reprocessing },
            ],
            filteredValue: tableParams.filters.status
                ? (Array.isArray(tableParams.filters.status) ? tableParams.filters.status : [tableParams.filters.status])
                : null,
            render: (_: any, { id, status, message }: ITableData) => (
                <StatusTag
                    status={status}
                    message={message}
                    key={`tag-${id}`}
                />
            ),
            width: 160,
        },
        {
            title: 'Ações',
            key: 'actions',
            width: 140,
            align: 'center' as const,
            fixed: 'right' as const,
            render: (_: any, record: ITableData) => (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                    {record.link && verifyLinkIsValid({ link: record.link }) && (
                        <TableActionButton
                            action="custom"
                            icon={<FaLink style={{ fontSize: 13, color: '#1677ff' }} />}
                            tooltip="Abrir link da nota"
                            onClick={(e) => {
                                e?.stopPropagation?.();
                                window.open(normalizeLink(record.link), '_blank');
                            }}
                        />
                    )}
                    {record.key && (
                        <TableActionButton
                            action="custom"
                            icon={<FaCopy style={{ fontSize: 13, color: '#8c8c8c' }} />}
                            tooltip="Copiar chave"
                            onClick={(e) => {
                                e?.stopPropagation?.();
                                navigator.clipboard.writeText(String(record.key));
                                notification.success({
                                    type: 'success',
                                    message: "Chave copiada!",
                                    description: (
                                        <a href={getLinkInvoice(record.type as any)} target="_blank" rel="noopener noreferrer">
                                            Abrir link
                                        </a>
                                    ),
                                    duration: 3,
                                });
                            }}
                        />
                    )}
                    <TableActionButton
                        action="custom"
                        icon={<FaUser style={{ fontSize: 13, color: '#10b981' }} />}
                        tooltip="Ver usuário"
                        bgColor={isDark ? 'rgba(16, 185, 129, 0.12)' : '#ecfdf5'}
                        onClick={(e) => {
                            e?.stopPropagation?.();
                            navigate('/usuarios/aplicativo/visualizar/' + record.appUserId);
                        }}
                    />
                    {record.status === EInvoiceStatus.processing && (
                        <TableActionButton
                            action="custom"
                            icon={<FaSync style={{ fontSize: 13, color: '#faad14' }} />}
                            tooltip="Reprocessar nota"
                            bgColor={isDark ? 'rgba(250, 173, 20, 0.12)' : '#fffbe6'}
                            onClick={(e) => {
                                e?.stopPropagation?.();
                                reprocessInvoice(record.id, record.appUserId);
                            }}
                        />
                    )}
                </div>
            ),
        },
    ].filter(Boolean) as ColumnsType<ITableData>;

    const setTableDataFromRequest = useCallback((data: Array<IInvoiceWithFilters>) => {
        const newData = data.map((invoice) => ({
            ...invoice,
            rowKey: invoice.id,
            appUserName: invoice.app_user_name,
            appUserId: invoice.app_user_id,
            createdAt: invoice.created_at,
            date_issue: invoice.date_issue,
        }));
        setTableData(newData);
    }, []);

    useEffect(() => {
        const controller = new AbortController();

        async function fetchInvoices() {
            try {
                setIsLoading(true);
                const response = await InvoicesService.listAllByCity({
                    cityId: activeCity?.id!,
                    page: tableParams.pagination.current,
                    size: tableParams.pagination.pageSize,
                    sort: tableParams.sorter.field as TypeSortInvoice,
                    order: tableParams.sorter.order === 'asc' ? 'asc' : 'desc',
                    status: Array.isArray(tableParams.filters.status)
                        ? tableParams.filters.status
                        : tableParams.filters.status
                        ? [tableParams.filters.status]
                        : undefined,
                    type: tableParams.filters.type || undefined,
                    user: tableParams.filters.appUserName || undefined,
                    company: tableParams.filters.company ?? location.state?.company ?? undefined,
                    createdAt: (tableParams.filters.createdAt && convertDataBrParaUS(tableParams.filters.createdAt)) || undefined,
                    dateIssue: (tableParams.filters.date_issue && convertDataBrParaUS(tableParams.filters.date_issue)) || undefined,
                    date_issue: (tableParams.filters.date_issue && convertDataBrParaUS(tableParams.filters.date_issue)) || undefined,
                    startPeriod: (startPeriod && convertDataBrParaUS(startPeriod)) || undefined,
                    endPeriod: (endPeriod && convertDataBrParaUS(endPeriod)) || undefined,
                    signal: controller.signal,
                });

                setTableDataFromRequest(response.invoices);
                setTotalInvoices(response.total);
                setIsLoading(false);
            } catch (err) {
                if (!(err instanceof AxiosError && err.name === 'CanceledError')) {
                    EmitError(err, true, 'cityInvoices');
                    setIsLoading(false);
                }
            }
        }

        fetchInvoices();

        return () => controller.abort();
    }, [activeCity, tableParams, setTableDataFromRequest, startPeriod, endPeriod, location.state?.company]);

    return {
        columns,
        isLoading,
        tableData,
        handleTableChange,
        handlePaginationChange,
        handleClearAllFilters,
        activeFilterItems,
        tableParams,
        totalInvoices,
    };
}

